/**
 * Calcolatore Interesse Composto
 * Real-time compound interest calculator with Chart.js visualization.
 */

import { handleShareClick } from '/js/shareLink.js';
import { t, PAGE_LANG, UI_LOCALE, registerTranslations } from '/js/i18n.js';

// Dizionario locale del calcolatore (chiavi = stringhe italiane).
registerTranslations({
  'Annuale': 'Yearly',
  'Semestrale': 'Half-yearly',
  'Trimestrale': 'Quarterly',
  'Mensile': 'Monthly',
  'Ora': 'Now',
  'Anno {y}': 'Year {y}',
  'dopo {years} anni al {rate}%': 'after {years} years at {rate}%',
  'sul capitale di {amount}': 'on a principal of {amount}',
  'Mostra meno ▲': 'Show less ▲',
  'Mostra tutti gli anni ({n}) ▼': 'Show all years ({n}) ▼',
  'Valore reale': 'Real value',
  'Capitale totale': 'Total balance',
  'Versamenti totali': 'Total deposits',
  'Interessi maturati': 'Interest earned',
  '{n} anni': '{n} years',
  'Importo per versamento {freq}': 'Amount per {freq} contribution'
});

const SHARE_CAMPAIGN = 'ic_sharing';

const encodeParams = (obj) => {
  try {
    return btoa(encodeURIComponent(JSON.stringify(obj)));
  } catch (e) {
    return null;
  }
};

const decodeParams = (encoded) => {
  try {
    return JSON.parse(decodeURIComponent(atob(encoded)));
  } catch (e) {
    return null;
  }
};

let chart = null;
let showAllRows = false;
let allRows = [];

const fmt = v =>
  '€\u202f' + Math.round(v).toLocaleString(UI_LOCALE);

// Separatore decimale coerente con la lingua della pagina (7.0 -> "7,0" solo in italiano).
const fmtDecimal = v => (PAGE_LANG === 'en' ? v : v.replace('.', ','));

const freqValues = [1, 2, 4, 12];
const freqLabels = [t('Annuale'), t('Semestrale'), t('Trimestrale'), t('Mensile')];

// Contribution frequency: from most frequent (monthly) to least (annual)
const contribFreqValues = [12, 4, 2, 1];
const contribFreqLabels = [t('Mensile'), t('Trimestrale'), t('Semestrale'), t('Annuale')];

function getFreqFromIndex(i) {
  return freqValues[Math.max(0, Math.min(3, i))];
}

function getContribFreqFromIndex(i) {
  return contribFreqValues[Math.max(0, Math.min(3, i))];
}

function updateSliderFill(el) {
  const min = parseFloat(el.min);
  const max = parseFloat(el.max);
  const val = parseFloat(el.value);
  const pct = ((val - min) / (max - min)) * 100;
  el.style.setProperty('--fill', pct + '%');
}

function getInputs() {
  return {
    P: parseFloat(document.getElementById('ic-principal').value) || 0,
    r: parseFloat(document.getElementById('ic-rate').value) / 100,
    Y: parseInt(document.getElementById('ic-years').value, 10),
    m: parseFloat(document.getElementById('ic-monthly').value) || 0,
    mFreq: getContribFreqFromIndex(parseInt(document.getElementById('ic-contrib-freq').value, 10)),
    infl: parseFloat(document.getElementById('ic-inflation').value) / 100,
    n: getFreqFromIndex(parseInt(document.getElementById('ic-freq').value, 10)),
  };
}

function updateUrl() {
  const el = (id) => document.getElementById(id);
  const params = {
    p: el('ic-principal').value,
    r: el('ic-rate').value,
    y: el('ic-years').value,
    m: el('ic-monthly').value,
    mf: el('ic-contrib-freq').value,
    n: el('ic-freq').value,
    i: el('ic-inflation').value,
  };
  const encoded = encodeParams(params);
  if (encoded) {
    const url = `${window.location.pathname}?d=${encoded}`;
    window.history.replaceState({}, '', url);
  }
  // Reset share button so a new short URL gets generated with updated params
  const btn = document.getElementById('ic-share-btn');
  if (btn) {
    delete btn.dataset.shareShortUrl;
    delete btn.dataset.shareStage;
  }
}

function loadFromUrl() {
  const encoded = new URLSearchParams(window.location.search).get('d');
  if (!encoded) return;
  const p = decodeParams(encoded);
  if (!p) return;
  const set = (id, val) => {
    const el = document.getElementById(id);
    if (el && val !== undefined) el.value = val;
  };
  set('ic-principal', p.p);
  set('ic-rate', p.r);
  set('ic-years', p.y);
  set('ic-monthly', p.m);
  set('ic-contrib-freq', p.mf);
  set('ic-freq', p.n);
  set('ic-inflation', p.i);
}

function calculate() {
  const { P, r, Y, m, mFreq, infl, n } = getInputs();

  // Rate per capitalization period
  const rn = r / n;
  // Effective rate per contribution period (accounts for capitalization between contributions)
  const rContrib = mFreq > 0 && r > 0 ? Math.pow(1 + rn, n / mFreq) - 1 : r / mFreq;

  const labels = [];
  const balances = [];
  const deposited = [];
  const interests = [];
  const realBalances = [];
  allRows = [];

  for (let y = 0; y <= Y; y++) {
    const periods = n * y;
    const contribPeriods = mFreq * y;
    const balance =
      P * Math.pow(1 + rn, periods) +
      (rContrib > 0
        ? m * ((Math.pow(1 + rContrib, contribPeriods) - 1) / rContrib)
        : m * contribPeriods);

    const dep = P + m * mFreq * y;
    const int = Math.max(0, balance - dep);

    const realBalance = infl > 0 ? balance / Math.pow(1 + infl, y) : null;

    labels.push(y === 0 ? t('Ora') : t('Anno\u00a0{y}', { y }));
    balances.push(Math.round(balance));
    deposited.push(Math.round(dep));
    interests.push(Math.round(int));
    realBalances.push(realBalance !== null ? Math.round(realBalance) : null);

    if (y > 0) {
      const real = infl > 0 ? balance / Math.pow(1 + infl, y) : balance;
      allRows.push({
        y,
        dep: Math.round(dep),
        int: Math.round(int),
        bal: Math.round(balance),
        real: Math.round(real),
      });
    }
  }

  const finalBal = balances[Y];
  const dep = deposited[Y];
  const int = finalBal - dep;
  const realVal = infl > 0 ? finalBal / Math.pow(1 + infl, Y) : finalBal;
  const multi = dep > 0 ? finalBal / dep : 0;

  // Update metric cards
  const rateDisplay = fmtDecimal(parseFloat(document.getElementById('ic-rate').value).toFixed(1));

  document.getElementById('ic-r-final').textContent = fmt(finalBal);
  document.getElementById('ic-r-final-sub').textContent =
    t('dopo {years} anni al {rate}%', { years: Y, rate: rateDisplay });
  document.getElementById('ic-r-interest').textContent = fmt(int);
  document.getElementById('ic-r-interest-sub').textContent =
    t('sul capitale di {amount}', { amount: fmt(P) });
  document.getElementById('ic-r-deposited').textContent = fmt(dep);
  document.getElementById('ic-r-multi').textContent =
    fmtDecimal(multi.toFixed(2)) + '\u00d7';
  document.getElementById('ic-r-real-val').textContent = fmt(realVal);

  renderTable(showAllRows);
  updateLegend(infl);
  renderChart(labels, balances, deposited, interests, realBalances, infl);
  updateUrl();
}

function renderTable(all) {
  const rows = all ? allRows : allRows.slice(0, 10);
  const tbody = document.getElementById('ic-tbody');
  tbody.innerHTML = rows
    .map(
      r => `
    <tr>
      <td>${r.y}</td>
      <td class="num">${fmt(r.dep)}</td>
      <td class="num green">+${fmt(r.int)}</td>
      <td class="num">${fmt(r.bal)}</td>
      <td class="num muted">${fmt(r.real)}</td>
    </tr>`
    )
    .join('');

  const btn = document.getElementById('ic-show-more');
  if (allRows.length <= 10) {
    btn.style.display = 'none';
    return;
  }
  btn.style.display = 'block';
  btn.textContent = all
    ? t('Mostra meno \u25b2')
    : t('Mostra tutti gli anni ({n}) \u25bc', { n: allRows.length });
}

function updateLegend(infl) {
  const legend = document.getElementById('ic-legend');
  const realEntry = legend.querySelector('.ic-legend-real');
  if (infl > 0 && !realEntry) {
    const span = document.createElement('span');
    span.className = 'ic-legend-real';
    span.innerHTML = '<i style="background:#a855f7;opacity:0.7"></i> ' + t('Valore reale');
    legend.appendChild(span);
  } else if (infl === 0 && realEntry) {
    realEntry.remove();
  }
}

function renderChart(labels, balances, deposited, interests, realBalances, infl) {
  const ctx = document.getElementById('ic-chart').getContext('2d');
  if (chart) {
    chart.destroy();
  }
  // Chart is loaded via CDN as global
  chart = new window.Chart(ctx, {
    type: 'line',
    data: {
      labels,
      datasets: [
        {
          label: t('Capitale totale'),
          data: balances,
          borderColor: '#214ccd',
          backgroundColor: 'rgba(33,76,205,0.07)',
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 2.5,
        },
        {
          label: t('Versamenti totali'),
          data: deposited,
          borderColor: '#f59e0b',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 1.5,
          borderDash: [5, 4],
        },
        {
          label: t('Interessi maturati'),
          data: interests,
          borderColor: '#16a34a',
          backgroundColor: 'rgba(22,163,74,0.06)',
          fill: true,
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 2,
        },
        ...(infl > 0 ? [{
          label: t('Valore reale'),
          data: realBalances,
          borderColor: '#a855f7',
          backgroundColor: 'transparent',
          fill: false,
          tension: 0.35,
          pointRadius: 0,
          borderWidth: 2,
          borderDash: [4, 3],
        }] : []),
      ],
    },
    options: {
      responsive: true,
      maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { display: false },
        tooltip: {
          backgroundColor: '#fff',
          borderColor: '#e2e8f0',
          borderWidth: 1,
          titleColor: '#1a1a1a',
          bodyColor: '#4b5563',
          padding: 10,
          callbacks: {
            label: ctx => ' ' + ctx.dataset.label + ': ' + fmt(ctx.parsed.y),
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          ticks: {
            maxTicksLimit: 10,
            font: { size: 11, family: 'Inter, -apple-system, sans-serif' },
            color: '#94a3b8',
          },
        },
        y: {
          grid: { color: '#f1f5f9' },
          ticks: {
            font: { size: 11, family: 'Inter, -apple-system, sans-serif' },
            color: '#94a3b8',
            callback: v =>
              v >= 1_000_000
                ? '€' + (v / 1_000_000).toFixed(1) + 'M'
                : v >= 1_000
                ? '€' + (v / 1_000).toFixed(0) + 'k'
                : '€' + v,
          },
        },
      },
    },
  });
}

function initSlider(id, displayId, formatter) {
  const el = document.getElementById(id);
  const disp = document.getElementById(displayId);
  updateSliderFill(el);
  disp.textContent = formatter(el.value);
  el.addEventListener('input', () => {
    updateSliderFill(el);
    disp.textContent = formatter(el.value);
    calculate();
  });
}

function init() {
  // Load URL params first so initSlider reads the correct values for fill/label
  loadFromUrl();

  // Rate slider: 0–20%
  initSlider('ic-rate', 'ic-rate-val', v =>
    fmtDecimal(parseFloat(v).toFixed(1)) + '%'
  );

  // Years slider: 1–50
  initSlider('ic-years', 'ic-years-val', v => t('{n} anni', { n: v }));

  // Freq slider: 4 steps mapped to [1, 2, 4, 12]
  initSlider('ic-freq', 'ic-freq-val', v => freqLabels[parseInt(v, 10)]);

  // Contribution frequency slider
  initSlider('ic-contrib-freq', 'ic-contrib-freq-val', v => contribFreqLabels[parseInt(v, 10)]);
  document.getElementById('ic-contrib-freq').addEventListener('input', function () {
    const label = contribFreqLabels[parseInt(this.value, 10)].toLowerCase();
    document.getElementById('ic-monthly-label').textContent = t('Importo per versamento {freq}', { freq: label });
  });

  // Number inputs
  ['ic-principal', 'ic-monthly', 'ic-inflation'].forEach(id => {
    document.getElementById(id).addEventListener('input', calculate);
  });

  // Show more table toggle
  document.getElementById('ic-show-more').addEventListener('click', () => {
    showAllRows = !showAllRows;
    renderTable(showAllRows);
  });

  // Share button
  const shareBtn = document.getElementById('ic-share-btn');
  if (shareBtn) {
    shareBtn.addEventListener('click', () =>
      handleShareClick(shareBtn, { campaign: SHARE_CAMPAIGN })
    );
  }

  calculate();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', init);
} else {
  init();
}
